import { useState } from "react";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function BulkUserImport() {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleImport = async () => {
    setIsLoading(true);
    setResults(null);

    try {
      const { data, error } = await digitalOcean.functions.invoke('bulk-import-users');

      if (error) throw error;

      setResults(data.results);
      
      // Success: success === true AND userCreated !== true (covers undefined and false)
      const successCount = data.results.filter(r => r.success === true && r.userCreated !== true).length;
      // Partial success: success === false AND userCreated === true (user exists but setup incomplete)
      const partialSuccessCount = data.results.filter(r => r.success === false && r.userCreated === true).length;
      // Error: has error AND userCreated !== true (complete failure, no user created)
      const errorCount = data.results.filter(r => r.error && r.userCreated !== true).length;
      
      // Show error toast if all imports failed
      if (errorCount > 0 && successCount === 0 && partialSuccessCount === 0) {
        toast.error(`Import failed! ${errorCount} errors. No users were created.`);
      } else if (partialSuccessCount > 0) {
        toast.warning(`Import complete! ${successCount} users created successfully, ${partialSuccessCount} with partial success (user created but setup incomplete), ${errorCount} errors.`);
      } else {
        toast.success(`Import complete! ${successCount} users created, ${errorCount} errors.`);
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.message || "Failed to import users");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-foreground mb-8">Bulk User Import</h1>
        
        <div className="neuro-base rounded-3xl p-8 space-y-6">
          <div className="space-y-4">
            <p className="text-muted-foreground">
              This will create all user accounts with the following data:
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
              <li>10 user accounts will be created</li>
              <li>Passwords will be set to match each user&apos;s email address</li>
              <li>Users will be prompted to change their password on first login</li>
              <li>Profile data and social links will be populated</li>
              <li>Tracks will be assigned to the correct artists</li>
              <li>Admin role will be assigned to knoedlerjosiahd@gmail.com</li>
            </ul>
          </div>

          <Button
            onClick={handleImport}
            disabled={isLoading}
            className="w-full neuro-base active:neuro-pressed rounded-2xl py-6"
            tooltip={isLoading ? "Importing users..." : "Start bulk importing users from CSV"}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Importing Users...
              </>
            ) : (
              "Start Import"
            )}
          </Button>

          {results && (
            <div className="mt-8 space-y-4">
              <h2 className="text-2xl font-bold text-foreground">Import Results</h2>
              <div className="space-y-2">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-xl ${
                      result.success === true && result.userCreated !== true
                        ? "bg-green-500/10 text-green-400"
                        : result.success === false && result.userCreated === true
                        ? "bg-yellow-500/10 text-yellow-400"
                        : "bg-red-500/10 text-red-400"
                    }`}
                  >
                    <p className="font-semibold">{result.email}</p>
                    {result.success === true && result.userCreated !== true ? (
                      <p className="text-sm">✓ Created successfully (ID: {result.userId?.slice(0, 8)}...)</p>
                    ) : result.success === false && result.userCreated === true ? (
                      <div className="text-sm">
                        <p className="text-yellow-400">⚠ User created but setup incomplete (ID: {result.userId?.slice(0, 8)}...)</p>
                        {result.error && (
                          <p className="mt-1 text-yellow-300">Error: {result.error}</p>
                        )}
                        {result.note && (
                          <p className="mt-1 text-yellow-300 text-xs">{result.note}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm">✗ Error: {result.error}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
